import { Button } from '@fountain/fountain-ui-components';
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StyledPagination } from 'components/StyledPagination';
import globalMessages from 'shared/global/messages';

import ruleMessages from '../messages';
import { SubjectTypeChip } from '../SubjectTypeChip';
import { OFFER, OPENING } from '../types';
import { ApprovalStatusCell } from './ApprovalStatusCell';
import { BulkDecisionBar } from './BulkDecisionBar';
import { Decision } from './DecisionFooter';
import messages from './messages';
import { OfferReviewPanel } from './OfferReviewPanel';
import { OpeningActionsCell } from './OpeningActionsCell';
import { QueueFilterMenu, QueueFilterOption } from './QueueFilterMenu';
import { QueueSearchField } from './QueueSearchField';
import { RejectOpeningDialog } from './RejectOpeningDialog';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { useStyles } from './styles';
import { ApprovalRequest } from './types';
import {
  ALL,
  StatusFilter,
  TypeFilter,
  useApprovalRequests,
} from './useApprovalRequests';
import { useBulkDecision } from './useBulkDecision';
import { isSelectable, useQueueSelection } from './useQueueSelection';

const TYPE_OPTIONS: Array<QueueFilterOption<TypeFilter>> = [
  { value: ALL, label: messages.filterAllTypes },
  { value: OFFER, label: ruleMessages.typeOffer },
  { value: OPENING, label: ruleMessages.typeOpening },
];

const STATUS_OPTIONS: Array<QueueFilterOption<StatusFilter>> = [
  { value: ALL, label: messages.filterAllStatuses },
  { value: 'pending', label: messages.statusPending },
  { value: 'approved', label: globalMessages.approved },
  { value: 'rejected', label: messages.statusRejected },
  { value: 'cancelled', label: messages.statusCancelled },
];

/**
 * The checkbox, Opening, Type, Approver rule, Approval status, and the Actions
 * cell — so a state rendered in the table body spans the full width rather than
 * sitting under the first column.
 */
const COLUMN_COUNT = 6;

/**
 * One waiting approval (Design Requirements §5).
 *
 * ## The first column names the opening, whatever the row is about
 *
 * Not the row's subject. An offer prints the opening it is on with the applicant
 * beneath it; an opening prints the same opening with nothing beneath it — which
 * is why the header reads "Opening" rather than something that would have to
 * change per row. The server answers both from one field for the same reason.
 *
 * ## An offer opens a panel; an opening decides in place
 *
 * An offer is a document somebody has to read before deciding, so its row opens
 * the review panel. An opening has none — the rule and its levels are already in
 * the two columns to the left — so it carries Approve and Reject directly. See
 * `OpeningActionsCell`.
 *
 * The Review control is not gated on whether this viewer can decide: the panel is
 * readable either way, and says which of the three reasons it is showing no
 * decision controls. The opening's controls are gated, because there is nothing
 * else in the cell for somebody with no open assignment to read.
 */
const QueueRow: React.FC<{
  row: ApprovalRequest;
  selected: boolean;
  /** True while a bulk decision is in flight — see `OpeningActionsCell`. */
  busy: boolean;
  onReview: () => void;
  onDecided: () => void;
  onToggle: () => void;
  /** True while this row has a decision of its own in flight. */
  deciding: boolean;
  onDecidingChange: (id: string, deciding: boolean) => void;
}> = ({
  row,
  selected,
  busy,
  deciding,
  onReview,
  onDecided,
  onToggle,
  onDecidingChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  // The row's own identity, for the accessible name of a control that is called
  // "Review" on every row. The applicant first: on an offer row that is the most
  // identifying thing, and every offer has one.
  const rowLabel = row.subject.applicantName ?? row.subject.positionName ?? '';

  return (
    <TableRow data-testid="approval-request-row">
      {/* Empty on a row a bulk decision cannot act on, rather than disabled: a
          disabled checkbox on every offer row reads as a permission the reader
          might gain, and there is nothing they could do to gain it. */}
      <TableCell className={styles.selectCell}>
        {isSelectable(row) && (
          <Checkbox
            checked={selected}
            color="primary"
            disabled={busy || deciding}
            inputProps={{
              'aria-label': intl.formatMessage(messages.selectRow, {
                opening: row.subject.positionName ?? '',
              }),
            }}
            size="small"
            onChange={onToggle}
          />
        )}
      </TableCell>

      <TableCell>
        <Typography variant="body1">{row.subject.positionName}</Typography>
        {/* Absent on an opening row rather than blank: an opening is not about a
            person, so there is nothing for the line to hold. */}
        {row.subject.applicantName && (
          <Typography color="textSecondary" variant="body2">
            {row.subject.applicantName}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <SubjectTypeChip subjectType={row.subjectType} testId="request-type" />
      </TableCell>

      <TableCell>
        {/* One line per rule. Plural on purpose: several rules can gate one
            subject, and each contributes its own levels to the cell beside this
            one — in the same order, so the two read across. A rule the pinned
            snapshot could not name renders nothing rather than a placeholder;
            its levels are still listed. */}
        <div className={styles.ruleNames} data-testid="approver-rules">
          {row.rules.map(
            rule =>
              rule.name && (
                <Typography key={rule.id ?? rule.name} variant="body2">
                  {rule.name}
                </Typography>
              ),
          )}
        </div>
      </TableCell>

      <TableCell>
        {/* `status !== 'pending'` is the whole of "settled" — approved, rejected
            and cancelled all end the request, and a cancelled one leaves its
            open level reading `active`. */}
        <ApprovalStatusCell
          requestSettled={row.status !== 'pending'}
          rules={row.rules}
        />
      </TableCell>

      <TableCell className={styles.actionsCell}>
        {row.subjectType === OFFER && (
          <Button
            autoWidth
            size="small"
            type="secondary"
            // Every row's control reads "Review", so the accessible name carries
            // the row it opens — otherwise a screen reader user hears the same
            // name N times with nothing telling them apart.
            aria-label={intl.formatMessage(messages.reviewOfferFor, {
              applicant: rowLabel,
            })}
            onClick={onReview}
          >
            <FormattedMessage {...messages.reviewOffer} />
          </Button>
        )}
        {row.subjectType === OPENING && (
          <OpeningActionsCell
            disabled={busy}
            row={row}
            onDecided={onDecided}
            onDecidingChange={onDecidingChange}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

/**
 * The approvals a recruiter or approver has a stake in, filtered and paged.
 *
 * Offers and openings together, filtered and searched server-side and paged.
 * An offer row opens the review panel, because an offer is a document somebody
 * has to read; an opening is decided from the row itself, alone or in bulk.
 */
export const ApprovalsQueue: React.FC = () => {
  const styles = useStyles();
  const intl = useIntl();
  // Which request the panel is showing. One piece of state, so "two panels open"
  // is unrepresentable.
  const [reviewing, setReviewing] = useState<string | null>(null);

  // On by default: the queue's job is "what needs me" (Design Requirements §5).
  const [mine, setMine] = useState(true);
  const [type, setType] = useState<TypeFilter>(ALL);
  const [status, setStatus] = useState<StatusFilter>(ALL);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useApprovalRequests({
    mine,
    type,
    status,
    search,
    page,
  });

  const rows = data?.rows ?? [];
  // The pager is the one control read from the payload rather than from
  // `isLoading`, so it is the one that has to survive a reload — see
  // `useApprovalRequests`, which is where the payload is held across one.
  const currentPage = data?.currentPage ?? page;
  const totalPages = data?.totalPages ?? 1;

  // Which rows are deciding on their own, owned here rather than per row: the
  // selection has to exclude them, and Select All builds its set from what the
  // selection calls selectable.
  const [decidingIds, setDecidingIds] = useState<ReadonlySet<string>>(new Set());
  const setDeciding = useCallback((id: string, deciding: boolean) => {
    setDecidingIds(current => {
      if (current.has(id) === deciding) return current;

      const next = new Set(current);
      if (deciding) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selection = useQueueSelection(rows, decidingIds);
  // Whether the reject dialog is open for the selection. The single-row dialog is
  // the row's own state — one piece of state each, so "both open" is
  // unrepresentable per surface.
  const [rejectingSelection, setRejectingSelection] = useState(false);
  const bulk = useBulkDecision({
    onSettled: () => {
      setRejectingSelection(false);
      selection.clear();
      refetch();
    },
  });

  const decideSelection = (decision: Decision, comment?: string) =>
    bulk.run(selection.selected, decision, comment);

  // Everything that could change what a run is deciding, or land its completion
  // somewhere else, is held while it is in flight: the rows' own controls, the
  // checkboxes, the pager and the filters. The alternative was cancelling the run
  // on a page turn, which cannot un-commit the decisions that already landed.
  const busy = bulk.running !== null;

  // Any filter change invalidates the page: page 3 of the unfiltered queue is
  // very often past the end of the filtered one, and a request that kept the
  // page would answer with nothing while the toolbar claimed a filter had
  // matched nothing.
  //
  // It also invalidates the selection, and so does a page turn: `useQueueSelection`
  // will not act on a row that is off screen, but a count that survives the rows
  // it counted is a count the reader has to reconcile against a table that no
  // longer shows them.
  const applyFilter = (change: () => void) => {
    change();
    setPage(1);
    selection.clear();
    bulk.clearOutcome();
  };

  const goToPage = (next: number) => {
    setPage(next);
    selection.clear();
    bulk.clearOutcome();
  };

  const isFiltered = type !== ALL || status !== ALL || search !== '';

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarFilters}>
          {/* A toggle, not one of a set: My approvals is on or off, which is
              exactly what `aria-pressed` expresses. The exclusive Offers |
              Openings control on the sibling rules tab is a radio group for the
              opposite reason — it can never be neither. */}
          <Button
            aria-pressed={mine}
            className={classNames(styles.filterPill, {
              [styles.filterPillActive]: mine,
            })}
            data-testid="my-approvals-toggle"
            disabled={busy}
            variant="outlined"
            onClick={() => applyFilter(() => setMine(current => !current))}
          >
            <FormattedMessage {...messages.myApprovals} />
          </Button>

          <QueueFilterMenu
            allValue={ALL}
            disabled={busy}
            label={messages.filterType}
            options={TYPE_OPTIONS}
            testId="type-filter"
            value={type}
            onChange={next => applyFilter(() => setType(next))}
          />

          <QueueFilterMenu
            allValue={ALL}
            disabled={busy}
            label={globalMessages.status}
            options={STATUS_OPTIONS}
            testId="status-filter"
            value={status}
            onChange={next => applyFilter(() => setStatus(next))}
          />
        </div>

        {/* The page reset lands here, synchronously with the new term, rather
            than inside the field's own debounce. */}
        <QueueSearchField
          value={search}
          onChange={next => applyFilter(() => setSearch(next))}
        />
      </div>

      {/* Only while something is selected, so it costs nothing when it is not in
          use — and it replaces the summary, because a bar offering a decision
          above a line reporting the last one reads as one statement. */}
      {bulk.running !== null || selection.selected.length > 0 ? (
        <BulkDecisionBar
          count={bulk.running ?? selection.selected.length}
          running={busy}
          onApprove={() => decideSelection('approved')}
          onClear={selection.clear}
          onReject={() => setRejectingSelection(true)}
        />
      ) : (
        bulk.outcome && (
          <div className={styles.bulkSummary}>
            {/* `status` when everything landed and `alert` when it did not: a
                partial run has something the reader must go and look at, and the
                two roles are how that difference reaches a screen reader. */}
            <Typography
              role={
                bulk.outcome.succeeded === bulk.outcome.requested
                  ? 'status'
                  : 'alert'
              }
              variant="body2"
            >
              {bulk.outcome.succeeded === bulk.outcome.requested ? (
                <FormattedMessage
                  {...(bulk.outcome.decision === 'approved'
                    ? messages.bulkAllApproved
                    : messages.bulkAllRejected)}
                  values={{ count: bulk.outcome.succeeded }}
                />
              ) : (
                <FormattedMessage
                  {...messages.bulkPartial}
                  values={{
                    succeeded: bulk.outcome.succeeded,
                    requested: bulk.outcome.requested,
                  }}
                />
              )}
            </Typography>
            <Button
              autoWidth
              size="small"
              type="secondary"
              onClick={bulk.clearOutcome}
            >
              <FormattedMessage {...globalMessages.close} />
            </Button>
          </div>
        )
      )}

      {/* Above the list, not inside it: the failure is about the request, not
          about a row, and burying it in the table body reads as "no approvals" —
          which is the one thing it does not mean. */}
      {isError && (
        <div className={styles.error} role="alert">
          <Typography variant="body1">
            <FormattedMessage {...messages.loadError} />
          </Typography>
          <Button autoWidth size="small" type="secondary" onClick={refetch}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      )}

      {/* One table for the loading, empty and populated states rather than a
          table for rows and a bare div for the rest: the column headers are what
          say what the rows will hold, so dropping them mid-request makes the
          region change shape twice per load.

          A failed request is the exception — the banner above has already
          explained itself, and headers over a zero-row body would read as a
          second fault.

          `role="region"` with a `tabIndex` because this container scrolls: the
          Position column holds account-authored text, so `overflowX: auto`
          genuinely engages on a narrow window, and in the loading and empty
          states there is nothing focusable inside at all (WCAG 2.1.1). */}
      {!isError && (
        <div
          aria-label={intl.formatMessage(messages.approvalsTab)}
          className={styles.list}
          role="region"
          tabIndex={0}
        >
          <Table
            aria-label={intl.formatMessage(messages.approvalsTab)}
            className={styles.table}
          >
            <TableHead>
              <TableRow>
                {/* Covers only the rows a bulk decision can act on. Absent when
                    there are none — a checkbox that selects nothing is a control
                    with no state to be in. */}
                <TableCell
                  className={classNames(styles.headerCell, styles.selectCell)}
                >
                  <span className={styles.visuallyHidden}>
                    <FormattedMessage {...messages.columnSelect} />
                  </span>
                  {selection.selectable.length > 0 && (
                    <SelectAllCheckbox
                      checked={
                        selection.selected.length ===
                        selection.selectable.length
                      }
                      // False when nothing is ticked AND when everything is: it
                      // is the "some" state, and a checkbox stuck in it says the
                      // page holds a selection nobody made.
                      indeterminate={
                        selection.selected.length > 0 &&
                        selection.selected.length <
                          selection.selectable.length
                      }
                      disabled={busy}
                      label={intl.formatMessage(messages.selectAll)}
                      onChange={selection.toggleAll}
                    />
                  )}
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnOpening} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...ruleMessages.columnType} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnApproverRule} />
                </TableCell>
                <TableCell className={styles.headerCell}>
                  <FormattedMessage {...messages.columnApprovalStatus} />
                </TableCell>
                {/* Named for a screen reader reading the table column by column,
                    and unnamed on screen: the control under it is
                    self-describing, and an empty `<th>` reads as a gap. */}
                <TableCell
                  className={classNames(styles.headerCell, styles.actionsCell)}
                >
                  <span className={styles.visuallyHidden}>
                    <FormattedMessage {...messages.columnActions} />
                  </span>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell
                    className={styles.stateCell}
                    colSpan={COLUMN_COUNT}
                  >
                    <CircularProgress
                      size={20}
                      aria-label={intl.formatMessage(messages.loadingApprovals)}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell className={styles.empty} colSpan={COLUMN_COUNT}>
                    {/* A filter that matched nothing is a different state from a
                        queue with nothing in it: one is recoverable by widening
                        the filter, the other by nobody. `layout-composition`
                        treats the no-data and zero-result cases as separate for
                        exactly that reason. */}
                    {/* `h3` for size — this theme's `h6` is 12px, which would
                        leave the title smaller than the sentence under it. */}
                    <Typography variant="h3">
                      {/* Quoting the term when there is one: "no approvals
                          match these filters" over a searched list leaves the
                          reader checking the menus for a narrowing that is
                          actually in the text field. */}
                      {search !== '' ? (
                        <FormattedMessage
                          {...messages.noSearchMatchesTitle}
                          values={{ term: search }}
                        />
                      ) : (
                        <FormattedMessage
                          {...(isFiltered
                            ? messages.noMatchesTitle
                            : messages.emptyTitle)}
                        />
                      )}
                    </Typography>
                    <Typography
                      className={styles.emptyBody}
                      color="textSecondary"
                      variant="body2"
                    >
                      <FormattedMessage
                        {...(isFiltered
                          ? messages.noMatchesBody
                          : messages.emptyBody)}
                      />
                    </Typography>
                    {/* The sentence above names the way out; this is that
                        sentence made clickable, which is what
                        `layout-composition` asks a filtered-empty state for.
                        Nothing on the genuinely-empty state: no action would make
                        rows appear. `mine` is deliberately not cleared — it is
                        the tab's own default, not a filter the reader set. */}
                    {isFiltered && (
                      <Button
                        autoWidth
                        className={styles.emptyAction}
                        size="small"
                        type="secondary"
                        onClick={() =>
                          applyFilter(() => {
                            setType(ALL);
                            setStatus(ALL);
                            setSearch('');
                          })
                        }
                      >
                        <FormattedMessage {...messages.clearFilters} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                rows.map(row => (
                  <QueueRow
                    busy={busy}
                    key={row.id}
                    row={row}
                    selected={selection.isSelected(row.id)}
                    deciding={decidingIds.has(row.id)}
                    onDecided={refetch}
                    onDecidingChange={setDeciding}
                    onReview={() => setReviewing(row.id)}
                    onToggle={() => selection.toggle(row.id)}
                  />
                ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Only past one page, per `layout-composition`: a pager over a single page
          of results is chrome with nothing behind it. No rows-per-page control —
          the endpoint fixes its own page size, so offering one would be a control
          the server ignores.

          This replaces the "Showing N of M" line the capped list carried. That
          line existed because the list was truncated and had to say so; a paged
          list is not partial, it is one page of a whole the pager can reach. */}
      {!isError && totalPages > 1 && (
        <StyledPagination
          containerClass={styles.pagination}
          currentPage={currentPage}
          disabled={busy}
          lastPage={totalPages}
          setCurrentPage={goToPage}
        />
      )}

      {/* One reason, recorded against every selected opening. The same dialog the
          single-row Reject opens — a second one is a second chance for the two to
          disagree about whether the reason is required. */}
      {rejectingSelection && (
        <RejectOpeningDialog
          count={selection.selected.length}
          openingName={selection.selected[0]?.subject.positionName ?? null}
          submitting={busy}
          onCancel={() => setRejectingSelection(false)}
          onConfirm={reason => decideSelection('rejected', reason)}
        />
      )}

      {/* Keyed so opening a different row remounts the panel: it holds the
          decision it recorded in its own state, and a reused instance would
          carry the previous offer's confirmation into the next one. */}
      {reviewing && (
        <OfferReviewPanel
          key={reviewing}
          requestId={reviewing}
          onClose={() => setReviewing(null)}
          // The row is gone from this list once the decision lands — the queue is
          // "waiting on you" — so the list is re-requested rather than patched.
          onDecided={refetch}
        />
      )}
    </>
  );
};

export default ApprovalsQueue;
